import React from "react";
import { Box, Divider, LinearProgress, Stack, Typography } from "@mui/material";
import { Calendar } from "react-multi-date-picker";
import DialogLayout from "../../Components/DialogLayout";
import Functions from "../../Helpers/Functions";
import useTailwindBrand from "../../Theme/useTailwindBrand";
import { months, weekDays } from "../../Utils/Function";
import { vtRemunerationLabel } from "../../Utils/VerifiedTrainerAmounts";
import { getActiveClientBrandConfig, getActivePrestationBrandConfig } from "../../config/environment.config";
import { useListCourse } from "../Courses/useListCourse";

type Montant = Pick<ReturnType<typeof useListCourse>,
    "teachr" | "montantOpen" | "setMontantOpen" | "trimestre" | "setTrimestre" | "montantPrice" | "montantPriceTtc" | "montantRemuneration"
    | "loadmontantPrice" | "duration" | "clientCount" | "montantDateFrom" | "montantDateTo" | "resetMontant">;

/**
 * Fenêtre « Montant total facturé » de l'onglet Passés, reprise telle quelle
 * de `ListCourse` : choix d'une période sur le calendrier, montant et
 * rémunération renvoyés par l'API, élèves distincts et heures.
 */
export default function MontantFactureDialog(m: Montant) {
    const { brand, brandConfig } = useTailwindBrand();
    const ff = { fontFamily: brandConfig.fontFamily };

    const content = (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 420, ...ff }}>
                Sélectionnez la période souhaitée pour obtenir une estimation du montant total facturé.
                Seuls les {getActivePrestationBrandConfig("s")} avec les statuts{" "}
                <strong>«&nbsp;Validé&nbsp;», «&nbsp;Prélevée&nbsp;» et «&nbsp;Collecté&nbsp;»</strong> sont
                pris en compte dans le calcul.
            </Typography>
            <Calendar
                months={months}
                weekDays={weekDays}
                format="DD/MM/YYYY"
                value={m.trimestre}
                onChange={(e: any) => m.setTrimestre(e)}
                range
                rangeHover
                className={brand === "verified-trainer" ? "teal" : ""}
            />
            {m.loadmontantPrice ? (
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={ff}>
                    Calcul en cours <LinearProgress aria-label="Loading…" />
                </Typography>
            ) : m.montantDateFrom && m.montantDateTo && (
                <Box sx={{ width: "100%", maxWidth: 380, p: 2.5, borderRadius: 2, bgcolor: "success.50", border: "1px solid", borderColor: "success.200", textAlign: "center", ...ff }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={ff}>Période sélectionnée</Typography>
                    <Typography variant="body2" fontWeight={600} mt={0.5} sx={ff}>du {m.montantDateFrom} au {m.montantDateTo}</Typography>
                    {m.montantPrice !== null && (
                        <>
                            <Divider sx={{ my: 1.5 }} />
                            {m.montantPriceTtc !== null && (
                                <>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={ff}>
                                        Prix total facturé (payé par vos clients + crédit d'impôt)
                                    </Typography>
                                    <Typography variant="h5" fontWeight={700} mt={0.5} sx={ff}>{m.montantPriceTtc}&nbsp;€</Typography>
                                    <Divider sx={{ my: 1.5 }} />
                                </>
                            )}
                            <Typography variant="caption" color="text.secondary" display="block" sx={ff}>
                                {m.montantPriceTtc !== null ? vtRemunerationLabel(m.teachr, "Votre rémunération") : "Montant total facturé"}
                            </Typography>
                            <Typography variant="h5" fontWeight={700} color="success.main" mt={0.5} sx={ff}>
                                {m.montantPriceTtc !== null ? (m.montantRemuneration ?? m.montantPrice) : m.montantPrice}&nbsp;€
                            </Typography>
                        </>
                    )}
                    {(m.clientCount !== null || m.duration !== null) && (
                        <>
                            <Divider sx={{ my: 1.5 }} />
                            <Stack direction="row" spacing={1.5} justifyContent="center">
                                {m.clientCount !== null && (
                                    <Box sx={{ flex: 1, p: 1.5, borderRadius: 2, bgcolor: "background.paper", border: "1px solid", borderColor: "success.200" }}>
                                        <Typography variant="h5" fontWeight={700} color="success.main" sx={ff}>{m.clientCount}</Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={ff}>
                                            {`${getActiveClientBrandConfig(m.clientCount > 1 ? "s" : "")} distinct${m.clientCount > 1 ? "s" : ""}`}
                                        </Typography>
                                    </Box>
                                )}
                                {m.duration !== null && (
                                    <Box sx={{ flex: 1, p: 1.5, borderRadius: 2, bgcolor: "background.paper", border: "1px solid", borderColor: "success.200" }}>
                                        <Typography variant="h5" fontWeight={700} color="success.main" sx={ff}>
                                            {m.duration > 0 ? Functions.renderDuration(m.duration) : "0h"}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={ff}>heures de cours</Typography>
                                    </Box>
                                )}
                            </Stack>
                        </>
                    )}
                </Box>
            )}
        </Box>
    );

    const close = () => { m.setMontantOpen(false); m.resetMontant(); };
    return (
        <DialogLayout
            title="💰 Montant Total Facturé"
            text={content}
            valid="C'est compris"
            cancel={m.montantDateFrom ? "Réinitialiser" : undefined}
            open={m.montantOpen}
            handleSubmit={close}
            handleClose={m.resetMontant}
            customCancel={m.montantDateFrom ? "w-full" : "hidden"}
            customValid="w-full"
            handleCloseIcon={close}
        />
    );
}
